import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../../../foundation/altzero_base/base/components/ui/dialog";
import { Button } from "../../../../foundation/altzero_base/base/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../../../foundation/altzero_base/base/components/ui/card";
import { Badge } from "../../../../foundation/altzero_base/base/components/ui/badge";
import { useToast } from "../../../../foundation/altzero_base/base/hooks/use-toast";
import {
  FileText,
  Upload,
  CheckCircle,
  BookOpen,
  Sparkles,
  X,
} from "lucide-react";

interface AdvancedTemplateUploadDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onWordUpload: (file: File) => void;
  onMarkdownUpload: (file: File) => void;
  isUploading?: boolean;
  uploadProgress?: number;
}

const WORD_FORMAT = {
  title: "Microsoft Word Document",
  description: "Upload .docx files with structured content",
  extensions: [".docx"],
  icon: FileText,
  color: "bg-blue-500",
  sample: `Project Proposal Template

Executive Summary
Brief overview of the project objectives and expected outcomes.

Project Background
Context about the client, industry, and project initiation reasons.

Scope of Work
Detailed description of included and excluded project scope.

Requirements & Deliverables
Specific requirements, features, and deliverables to be produced.

Timeline & Milestones
Project phases, key milestones, and delivery schedule.

Budget & Investment
Cost breakdown, payment terms, and investment details.

Technical Specifications
System requirements, technology stack, and architecture details.

Risk Assessment
Potential risks, mitigation strategies, and contingency plans.`,
  features: [
    "Automatic section detection from headings",
    "Preserves original formatting and styles",
    "Supports tables, lists, and images",
    "Extracts document structure automatically"
  ]
};

export const AdvancedTemplateUploadDialog: React.FC<AdvancedTemplateUploadDialogProps> = ({
  isOpen,
  onClose,
  onWordUpload,
  onMarkdownUpload,
  isUploading = false,
  uploadProgress = 0
}) => {
  const [dragOver, setDragOver] = useState(false);
  const { toast } = useToast();

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const isWordFile = file.name.toLowerCase().endsWith('.docx');

    if (!isWordFile) {
      toast({
        title: "Invalid File Type",
        description: "Please upload a .docx file only",
        variant: "destructive",
      });
      return;
    }

    onWordUpload(file);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(false);

    const file = event.dataTransfer.files[0];
    if (!file) return;

    const isWordFile = file.name.toLowerCase().endsWith('.docx');

    if (!isWordFile) {
      toast({
        title: "Invalid File Type",
        description: "Please upload a .docx file only",
        variant: "destructive",
      });
      return;
    }

    onWordUpload(file);
  };

  const currentFormat = WORD_FORMAT;
  const IconComponent = currentFormat.icon;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-blue-500" />
            Upload Sample Template
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            Upload a Word document (.docx) to create a new template with automatic section detection.
          </p>
        </DialogHeader>

        {/* Upload Area - MOVED TO TOP */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Upload Your Word Document
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                dragOver
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
            >
              <div className="space-y-4">
                <div className={`mx-auto w-12 h-12 rounded-full ${currentFormat.color} flex items-center justify-center text-white`}>
                  <IconComponent className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-lg font-medium">
                    Drop your Word document here
                  </p>
                  <p className="text-sm text-muted-foreground">
                    or click to browse files
                  </p>
                </div>
                <input
                  type="file"
                  accept=".docx"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="template-upload"
                  disabled={isUploading}
                />
                <Button
                  onClick={() => document.getElementById('template-upload')?.click()}
                  disabled={isUploading}
                  size="lg"
                  className="mx-auto"
                >
                  {isUploading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                      Processing Document...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Choose Word Document
                    </>
                  )}
                </Button>

                {/* Progress Bar */}
                {isUploading && (
                  <div className="w-full max-w-md mx-auto">
                    <div className="flex justify-between text-sm text-gray-600 mb-2">
                      <span>Uploading...</span>
                      <span>{uploadProgress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-500 h-2 rounded-full transition-all duration-300 ease-out"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                    <div className="text-xs text-gray-500 mt-2 text-center">
                      {uploadProgress < 30 ? 'Uploading file...' :
                       uploadProgress < 70 ? 'Processing document...' :
                       uploadProgress < 90 ? 'Extracting sections...' :
                       'Almost done...'}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-6">
          {/* Format Info Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${currentFormat.color} text-white`}>
                  <IconComponent className="w-5 h-5" />
                </div>
                {currentFormat.title}
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                {currentFormat.description}
              </p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium mb-2">Supported Format</h4>
                  <div className="flex gap-2">
                    {currentFormat.extensions.map((ext) => (
                      <Badge key={ext} variant="secondary">{ext}</Badge>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className="font-medium mb-2">Features</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    {currentFormat.features.map((feature, index) => (
                      <li key={index} className="flex items-center gap-2">
                        <CheckCircle className="w-3 h-3 text-green-500" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Sample Word Document Structure */}
          <Card>
            <CardHeader>
              <CardTitle>Sample Word Document Structure</CardTitle>
              <p className="text-sm text-muted-foreground">
                Your Word document should follow this structure for best results
              </p>
            </CardHeader>
            <CardContent>
              <div className="bg-gray-50 rounded-lg p-4 max-h-64 overflow-y-auto">
                <div className="text-sm space-y-3">
                  <div className="font-bold text-lg">Project Proposal Template</div>

                  <div className="space-y-2">
                    <div className="font-semibold text-blue-600">Executive Summary</div>
                    <div className="text-gray-600 ml-4">Brief overview of the project objectives and expected outcomes.</div>
                  </div>

                  <div className="space-y-2">
                    <div className="font-semibold text-blue-600">Project Background</div>
                    <div className="text-gray-600 ml-4">Context about the client, industry, and project initiation reasons.</div>
                  </div>

                  <div className="space-y-2">
                    <div className="font-semibold text-blue-600">Scope of Work</div>
                    <div className="text-gray-600 ml-4">Detailed description of included and excluded project scope.</div>
                  </div>

                  <div className="space-y-2">
                    <div className="font-semibold text-blue-600">Requirements & Deliverables</div>
                    <div className="text-gray-600 ml-4">Specific requirements, features, and deliverables to be produced.</div>
                  </div>

                  <div className="space-y-2">
                    <div className="font-semibold text-blue-600">Timeline & Milestones</div>
                    <div className="text-gray-600 ml-4">Project phases, key milestones, and delivery schedule.</div>
                  </div>

                  <div className="space-y-2">
                    <div className="font-semibold text-blue-600">Budget & Investment</div>
                    <div className="text-gray-600 ml-4">Cost breakdown, payment terms, and investment details.</div>
                  </div>
                </div>
              </div>
              <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>💡 Tip:</strong> Use heading styles (Heading 1, Heading 2) in Word for automatic section detection.
                  Each heading will become a separate editable section in your template.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            <X className="w-4 h-4 mr-2" />
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AdvancedTemplateUploadDialog;
